import React from 'react';

import * as ArchiveActions from 'archive/ArchiveActions';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import {ArchiveOptionsForModal} from 'archive/components/modals/ArchiveModal';
import {ParentStorableData} from 'constants/SampleConstants';
import {getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import PrintLabelButton from 'label-printing/PrintLabelButton';
import {LocationInfo} from 'samples/locations/LocationViewConstants';
import ChangeParentStorableButton from 'samples/storables/ChangeParentStorableButton';
import StorableExportButton from 'samples/storables/StorableExportButton';
import {ButtonStyle} from 'ui-library/buttons/Button';

export interface LocationToolbarProps {
  location: LocationInfo;
  onSetParentStorable: (parentStorableData: ParentStorableData) => void;
  reload: () => void;
}

const BUTTON_STYLE = ButtonStyle.TERTIARY_GRAY;

/**
 * Toolbar for the location metadata page.
 */
export default function LocationToolbar({
  location,
  onSetParentStorable,
  reload,
}: LocationToolbarProps): JSX.Element {
  async function handleArchive(
    ids: Array<string>,
    purpose: string,
    options: ArchiveOptionsForModal
  ): Promise<void> {
    await ArchiveActions.bulkArchiveStorablesWithDataMods(ids, purpose, options);
    reload();
  }

  async function handleUnarchive(ids: Array<string>): Promise<void> {
    await ArchiveActions.bulkUnarchiveStorablesWithDataMods(ids);
    reload();
  }

  return (
    <div className="buttonToolbar">
      {location.permissions.writable && (
        <div className="buttonGroup">
          <ChangeParentStorableButton
            isWritable={location.permissions.writable}
            onSetParentStorable={onSetParentStorable}
            storableSchema={location.storableSchema}
            buttonStyle={BUTTON_STYLE}
            initialSelectedParentId={
              location.parentStorable && getAccessibleOrInaccessibleId(location.parentStorable)
            }
            allowNone={true}
            waitForSubmitErrors={true}
          />
        </div>
      )}
      <div className="buttonGroup">
        <PrintLabelButton buttonStyle={BUTTON_STYLE} itemIds={[location.id]} />
        <ArchiveOrUnarchiveButton
          archiveTooltipTitle="Archive location"
          unarchiveTooltipTitle="Unarchive location"
          buttonStyle={BUTTON_STYLE}
          isArchivable={Boolean(location.permissions.archivable)}
          isUnarchivable={Boolean(location.permissions.unarchivable)}
          item={location}
          onArchive={handleArchive}
          onUnarchive={handleUnarchive}
        />
        <StorableExportButton storableId={location.id} buttonStyle={BUTTON_STYLE} />
      </div>
    </div>
  );
}
